"use client"

import React from "react"
import { Navigate, useParams } from "react-router-dom"
import { Box, Flex, Text, Card, Heading } from "@radix-ui/themes"
import { AlertCircle } from "lucide-react"
import { useRestaurantAuth } from "../contexts/RestaurantAuthContext"
import { useLanguage } from "../contexts/LanguageContext"
import { hasRoutePermission, hasRoleAccess } from "../utils/permissions"
import LoadingSpinner from "./ui/dashboard/LoadingSpinner"

interface RouteGuardProps {
  children: React.ReactNode
  requiredPermissions?: string[]
  routeName?: string
}

const RouteGuard: React.FC<RouteGuardProps> = ({
  children,
  requiredPermissions = [],
  routeName,
}) => {
  const { user, isAuthenticated, loading, isOwner, hasPermission } = useRestaurantAuth()
  const { restaurantId } = useParams()
  const { t } = useLanguage()

  // Show loading while checking authentication
  if (loading) {
    return (
      <Flex direction="column" align="center" justify="center" style={{ minHeight: "100vh" }}>
        <LoadingSpinner size="3" text={t("checkingAuthentication")} />
      </Flex>
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/restaurant/login" replace />
  }

  // Restaurant owners have access to all routes
  if (isOwner) {
    return <>{children}</>
  }

  // Check route-level permission if routeName is provided
  if (routeName) {
    const userRole = user.role || ''

    // First check role-based access
    const hasRoleBasedAccess = hasRoleAccess(routeName, userRole, isOwner)

    // Then check permission-based access
    const hasPermissionAccess = hasRoutePermission(
      routeName,
      user.permissions as any,
      isOwner
    )

    // User needs both role access AND permission access
    if (!hasRoleBasedAccess || !hasPermissionAccess) {
      // Smart redirect: If a restricted staff member hits the dashboard URL,
      // bounce them to their allowed home page instead of showing an error.
      if (routeName === 'dashboard') {
        let route = '';
        if (userRole === 'cashier') route = 'orders';
        else if (userRole === 'kitchen_staff') route = 'kitchen-orders';
        else if (userRole === 'waiter') route = 'waiter-orders';

        if (route) {
          return <Navigate to={`/restaurant/${restaurantId}/${route}`} replace />
        }
      }

      return (
        <Flex direction="column" align="center" justify="center" style={{ minHeight: "100vh", padding: "2rem" }}>
          <Card size="4" style={{ maxWidth: 500 }}>
            <Flex direction="column" align="center" gap="4" p="6">
              <AlertCircle size={48} color="var(--red-9)" />
              <Heading size="6" align="center">
                {t("accessDenied")}
              </Heading>
              <Text size="3" color="gray" align="center">
                {t("insufficientPermissions")}
              </Text>
              <Text size="2" color="gray" align="center">
                {t("contactAdministratorForAccess")}
              </Text>
            </Flex>
          </Card>
        </Flex>
      )
    }
  }

  // Check specific permissions if provided
  if (requiredPermissions.length > 0) {
    const hasAllPermissions = requiredPermissions.every((permission) =>
      hasPermission(permission)
    )

    if (!hasAllPermissions) {
      return (
        <Flex direction="column" align="center" justify="center" style={{ minHeight: "100vh", padding: "2rem" }}>
          <Card size="4" style={{ maxWidth: 500 }}>
            <Flex direction="column" align="center" gap="4" p="6">
              <AlertCircle size={48} color="var(--red-9)" />
              <Heading size="6" align="center">
                {t("accessDenied")}
              </Heading>
              <Text size="3" color="gray" align="center">
                {t("insufficientPermissions")}
              </Text>
              <Text size="2" color="gray" align="center">
                {t("requiredPermissions")}: {requiredPermissions.join(", ")}
              </Text>
            </Flex>
          </Card>
        </Flex>
      )
    }
  }

  return <>{children}</>
}

export default RouteGuard


